/**
 * MarketingPlan — /marketing/plan
 * Annual Marketing Plan Builder. Mi6 palette: black #0B0B0F · red #DC2626 · white/gray.
 */
import { useState } from "react";
import MarketingShell from "@/components/marketing/MarketingShell";
import MarketingAlignmentBlock from "@/components/marketing/MarketingAlignmentBlock";
import {
  ACTIVITY_TYPES,
  loadPlan,
  savePlan,
  type MarketingPlan as PlanType,
  type MarketingPlanRow,
} from "@/lib/marketing-hook-generator";
const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

function fmt$(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function NumInput({
  value,
  onChange,
  prefix,
}: {
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
}) {
  return (
    <div className="relative flex items-center">
      {prefix && (
        <span className="absolute left-2 text-neutral-500 text-xs pointer-events-none">{prefix}</span>
      )}
      <input
        type="number"
        min={0}
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full rounded border border-neutral-200 text-center text-sm text-neutral-800 focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] bg-white"
        style={{ paddingLeft: prefix ? "20px" : "6px", paddingRight: "6px", paddingTop: "4px", paddingBottom: "4px" }}
        placeholder="0"
      />
    </div>
  );
}

export default function MarketingPlan() {
  const [plan, setPlan] = useState<PlanType>(loadPlan);
  const [saved, setSaved] = useState(false);

  const rows = plan.rows;

  function updateRow(idx: number, field: keyof MarketingPlanRow, value: number) {
    const newRows = rows.map((r, i) =>
      i === idx ? { ...r, [field]: value } : r
    );
    setPlan({ ...plan, rows: newRows });
    setSaved(false);
  }

  function handleSave() {
    savePlan({ ...plan, savedAt: new Date().toISOString() });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  // Rollups
  const totalMqls    = rows.reduce((s, r) => s + (r.targetMqls || 0), 0);
  const totalSqls    = rows.reduce((s, r) => s + (r.targetSqls || 0), 0);
  const totalBudget  = rows.reduce((s, r) => s + (r.budget || 0), 0);
  const costPerMql   = totalMqls > 0 ? Math.round(totalBudget / totalMqls) : 0;

  return (
    <MarketingShell>
      {/* Page header */}
      <div className="bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] text-neutral-500 mb-0.5">Marketing Activity Plan</div>
            <h1 className="text-lg font-bold text-neutral-900">Annual Commitment Grid — 2026</h1>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-[11px] text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                ✓ Saved
              </span>
            )}
            <button
              onClick={handleSave}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all duration-150 hover:opacity-90 active:scale-95"
              style={{ background: MI6_RED }}
            >
              Save Plan
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex-1 w-full">

        {/* Rollup KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Annual MQL Target", value: totalMqls.toLocaleString(), unit: "MQLs" },
            { label: "Annual SQL Target", value: totalSqls.toLocaleString(), unit: "SQLs" },
            { label: "Annual Budget", value: fmt$(totalBudget), unit: "" },
            { label: "Cost Per MQL Target", value: totalMqls > 0 ? `$${costPerMql.toLocaleString()}` : "—", unit: "" },
          ].map((kpi) => (
            <div
              key={kpi.label}
              className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4"
              style={{ borderLeft: `4px solid ${MI6_BLACK}` }}
            >
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{kpi.label}</div>
              <div className="text-2xl font-bold tabular-nums" style={{ color: MI6_BLACK }}>{kpi.value}</div>
              {kpi.unit && <div className="text-xs text-neutral-500 mt-0.5">{kpi.unit}</div>}
            </div>
          ))}
        </div>

        {/* Main grid */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-sm border-collapse" style={{ minWidth: "960px" }}>
            <thead>
              <tr style={{ background: MI6_BLACK, color: "white" }}>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold w-48 sticky left-0 z-10" style={{ background: MI6_BLACK }}>Activity Type</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-20">Q1</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-20">Q2</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-20">Q3</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-20">Q4</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-24 text-neutral-300">Year Total</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-28">Target MQLs</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-28">Target SQLs</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-24 text-neutral-300">MQL→SQL %</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold w-32">Budget ($)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const yearTotal = (row.q1 || 0) + (row.q2 || 0) + (row.q3 || 0) + (row.q4 || 0);
                const isEven = idx % 2 === 0;
                return (
                  <tr
                    key={row.type}
                    className="border-b border-neutral-100 transition-colors duration-100 hover:bg-neutral-50/70"
                    style={{ background: isEven ? "white" : "#FAFAFA" }}
                  >
                    {/* Activity type label */}
                    <td className="px-4 py-2.5 font-medium text-neutral-700 text-xs sticky left-0 z-10" style={{ background: isEven ? "white" : "#FAFAFA" }}>
                      {row.type}
                    </td>
                    {/* Q1-Q4 inputs */}
                    {(["q1","q2","q3","q4"] as const).map((q) => (
                      <td key={q} className="px-2 py-2">
                        <NumInput value={row[q] as number} onChange={(v) => updateRow(idx, q, v)} />
                      </td>
                    ))}
                    {/* Year total — read-only */}
                    <td className="px-3 py-2 text-center font-bold tabular-nums" style={{ color: MI6_BLACK }}>
                      {yearTotal || "—"}
                    </td>
                    {/* Target MQLs */}
                    <td className="px-2 py-2">
                      <NumInput value={row.targetMqls} onChange={(v) => updateRow(idx, "targetMqls", v)} />
                    </td>
                    {/* Target SQLs */}
                    <td className="px-2 py-2">
                      <NumInput value={row.targetSqls} onChange={(v) => updateRow(idx, "targetSqls", v)} />
                    </td>
                    {/* MQL→SQL % */}
                    <td className="px-2 py-2">
                      <div className="relative flex items-center">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={row.mqlToSqlPct || ""}
                          onChange={(e) => updateRow(idx, "mqlToSqlPct", Number(e.target.value) || 25)}
                          className="w-full rounded border border-neutral-200 text-center text-sm text-neutral-500 focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] bg-white"
                          style={{ padding: "4px 6px" }}
                          placeholder="25"
                        />
                        <span className="absolute right-2 text-neutral-500 text-xs pointer-events-none">%</span>
                      </div>
                    </td>
                    {/* Budget */}
                    <td className="px-2 py-2">
                      <NumInput value={row.budget} onChange={(v) => updateRow(idx, "budget", v)} prefix="$" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Totals footer */}
            <tfoot>
              <tr style={{ background: "#0B0B0F", color: "white" }}>
                <td className="px-4 py-3 font-bold text-[11px] uppercase tracking-[0.15em] sticky left-0 z-10" style={{ background: "#0B0B0F" }}>Annual Totals</td>
                {(["q1","q2","q3","q4"] as const).map((q) => {
                  const sum = rows.reduce((s, r) => s + ((r[q] as number) || 0), 0);
                  return (
                    <td key={q} className="px-3 py-3 text-center font-bold tabular-nums text-sm">{sum || "—"}</td>
                  );
                })}
                <td className="px-3 py-3 text-center font-bold tabular-nums text-sm">
                  {rows.reduce((s, r) => s + (r.q1||0)+(r.q2||0)+(r.q3||0)+(r.q4||0), 0) || "—"}
                </td>
                <td className="px-3 py-3 text-center font-bold tabular-nums text-sm">{totalMqls || "—"}</td>
                <td className="px-3 py-3 text-center font-bold tabular-nums text-sm">{totalSqls || "—"}</td>
                <td className="px-3 py-3 text-center text-neutral-500 text-xs">—</td>
                <td className="px-3 py-3 text-center font-bold tabular-nums text-sm" style={{ color: "#FCA5A5" }}>{fmt$(totalBudget)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Legend + notes */}
        <div className="mt-4 text-xs text-neutral-500 flex items-center gap-4 flex-wrap">
          <span>· Year Total auto-sums Q1–Q4</span>
          <span>· MQL→SQL % defaults to 25% — override per row</span>
          <span>· Budget in USD</span>
          <span>· Last saved: {plan.savedAt ? new Date(plan.savedAt).toLocaleString() : "Not yet saved"}</span>
        </div>

        {/* Alignment to other plans — Top-10 ABM, BDR cadences, sales handoffs */}
        <MarketingAlignmentBlock />
      </main>
    </MarketingShell>
  );
}
